import { useSeedAllStudiedActorDeck } from "@-/dev.improve.haniverse.lib.cards.deck.all-studied";
import { useClearActorDeck } from "@-/dev.improve.haniverse.lib.cards.deck.clear";
import {
  PRESET_DECK_CHARACTERS,
  usePresetActorDeck,
} from "@-/dev.improve.haniverse.lib.cards.deck.preset";
import { useResetAllDatabases } from "@-/dev.improve.haniverse.lib.database.reset";
import { isAdminUserEmail } from "@-/dev.improve.haniverse.lib.admin.entities";
import { useAuthSession } from "@-/dev.improve.haniverse.lib.auth.client";
import { type UseMutationResult, useMutation } from "@-/com.npmjs.tanstack__react-query";
import { startProfiling, stopProfiling } from "@-/com.npmjs.react-native-release-profiler";
import { openProfileInBrowser } from "#open-profile-in-browser";
import { useMemo, useState } from "react";

const LARGE_DECK_GRAPHEME_COUNT = 125;

/**
 * A single admin action that can be triggered manually (via the admin controls
 * page) or from the Rozenite dev controls plugin. Both surfaces render the same
 * set of controls, so the descriptor carries everything either needs: copy, the
 * current status line, whether it is available, and the `run` handler.
 */
export interface AdminControl {
  /** Label for the action button (e.g. "Preset", "Clear deck"). */
  actionLabel: string;
  /** Longer explanation of what the action does. */
  description: string;
  /** Whether the action is currently runnable. */
  disabled: boolean;
  /** Stable identifier, also used as the Rozenite item id. */
  id: string;
  /** Runs the action; status updates via the underlying mutation state. */
  run: () => void;
  /** Latest status line ("Ready", "Seeding…", an error, etc.). */
  status: string;
  /** Short title shown as the row/section label. */
  title: string;
}

export interface AdminControlsState {
  /** The bundle the JS is running from — "development" or "production". */
  bundle: "development" | "production";
  controls: AdminControl[];
}

function formatAdminControlStatus<TData>(
  mutation: UseMutationResult<TData, Error, void, unknown>,
  labels: {
    error: (error: Error) => string;
    idle: string;
    pending: string;
    success: (data: TData | undefined, submittedAt: number) => string;
  },
): string {
  switch (mutation.status) {
    case "pending": {
      return labels.pending;
    }
    case "error": {
      return labels.error(mutation.error ?? new Error("Action failed"));
    }
    case "success": {
      return labels.success(mutation.data, mutation.submittedAt);
    }
    default: {
      return labels.idle;
    }
  }
}

function formatProfilingStatus(
  mutation: UseMutationResult<string | undefined, Error, void, unknown>,
  isProfiling: boolean,
): string {
  switch (mutation.status) {
    case "pending": {
      return isProfiling ? "Stopping…" : "Starting…";
    }
    case "error": {
      return mutation.error?.message ?? "Profiling failed";
    }
    case "success": {
      return mutation.data === undefined ? "Profiling" : `Saved — tap "Open in browser"`;
    }
    default: {
      return isProfiling ? "Profiling" : "Ready";
    }
  }
}

function formatOpenProfileStatus(
  mutation: UseMutationResult<{ speedscopeUrl: string }, Error, void, unknown>,
  lastProfilePath: string | null,
): string {
  switch (mutation.status) {
    case "pending": {
      return "Uploading…";
    }
    case "error": {
      return mutation.error?.message ?? "Open failed";
    }
    case "success": {
      return "Opened in browser";
    }
    default: {
      return lastProfilePath === null ? "Stop a profiling session first" : "Ready to upload & open";
    }
  }
}

/**
 * Centralises the dev/admin database actions (preset deck, clear deck, clear
 * database) so they can be exposed from multiple surfaces. Previously this lived
 * inline in the app layout's Rozenite plugin; it now backs both that plugin and
 * the in-app admin controls page.
 */
export function useAdminControls(): AdminControlsState {
  const { user } = useAuthSession();
  // The destructive deck/database controls run in any development bundle, and for
  // admin accounts on every build (staging and production included).
  const canRunDataControls = __DEV__ || isAdminUserEmail(user?.email);
  const [isProfiling, setIsProfiling] = useState(false);
  // Path of the most recent `.cpuprofile` saved by `stopProfiling(true)`. Kept so
  // the "Open in browser" control can upload it to the worker and open SpeedScope
  // without the user having to pull the file off the device.
  const [lastProfilePath, setLastProfilePath] = useState<string | null>(null);
  const presetActorDeckMutation = usePresetActorDeck();
  const seedAllStudiedActorDeckMutation = useSeedAllStudiedActorDeck({
    graphemeLimit: LARGE_DECK_GRAPHEME_COUNT,
  });
  const clearActorDeckMutation = useClearActorDeck();
  const resetAllDatabasesMutation = useResetAllDatabases();
  const profilingMutation = useMutation({
    mutationFn: async (): Promise<string | undefined> => {
      if (isProfiling) {
        return stopProfiling(true);
      }

      startProfiling();
      return undefined;
    },
    mutationKey: ["releaseProfiler"],
    onSuccess: (profilePath) => {
      setIsProfiling(profilePath === undefined);
      // `stopProfiling` resolves the saved path (start returns undefined). Remember
      // a fresh path; a new "Start" clears the old one so we never open a stale file.
      setLastProfilePath(profilePath ?? null);
    },
  });

  // Upload the last saved profile to the haniverse-worker and open the SpeedScope URL
  // it returns in the device browser — the "open in a browser from the device,
  // no cable / no --fromDownload" flow.
  const openProfileMutation = useMutation({
    mutationFn: async (): Promise<{ speedscopeUrl: string }> => {
      if (lastProfilePath === null) {
        throw new Error("No profile captured yet — stop a profiling session first.");
      }
      return openProfileInBrowser(lastProfilePath);
    },
    mutationKey: ["openReleaseProfile"],
  });

  const presetDeckStatus = formatAdminControlStatus(presetActorDeckMutation, {
    error: (error) => error.message,
    idle: "Ready",
    pending: "Seeding…",
    success: (seededCount = 0) => `Seeded ${seededCount ?? 0} cards — reload to see them`,
  });

  const seedAllStudiedDeckStatus = formatAdminControlStatus(seedAllStudiedActorDeckMutation, {
    error: (error) => error.message,
    idle: "Ready",
    pending: "Seeding all…",
    success: (result) =>
      `Seeded ${result?.seededCount ?? 0}/${result?.totalCount ?? 0} characters — reload to see them`,
  });

  const clearDeckStatus = formatAdminControlStatus(clearActorDeckMutation, {
    error: (error) => error.message,
    idle: "Ready",
    pending: "Clearing…",
    success: (_data, submittedAt) => `Cleared at ${new Date(submittedAt).toLocaleTimeString()}`,
  });

  const resetDataStatus = formatAdminControlStatus(resetAllDatabasesMutation, {
    error: (error) => error.message,
    idle: "Ready",
    pending: "Resetting…",
    success: (_data, submittedAt) => `Reset at ${new Date(submittedAt).toLocaleTimeString()}`,
  });
  const profilingStatus = formatProfilingStatus(profilingMutation, isProfiling);
  const openProfileStatus = formatOpenProfileStatus(openProfileMutation, lastProfilePath);

  const controls = useMemo<AdminControl[]>(
    () => [
      {
        actionLabel: isProfiling ? "Stop Profiling" : "Start Profiling",
        description:
          "Starts or stops a Hermes JS profiling session. Stopping saves a trace, then use “Open in browser” below to view it in SpeedScope.",
        disabled: profilingMutation.isPending,
        id: "release-profiler",
        run: () => {
          profilingMutation.mutate();
        },
        status: profilingStatus,
        title: "Release profiler",
      },
      {
        actionLabel: "Open in browser",
        description:
          "Uploads the last captured Hermes profile to the haniverse-worker and opens it in SpeedScope in your browser — no cable or CLI needed. Stop a profiling session first.",
        disabled: lastProfilePath === null || openProfileMutation.isPending,
        id: "open-release-profile",
        run: () => {
          openProfileMutation.mutate();
        },
        status: openProfileStatus,
        title: "Open profile in browser",
      },
      {
        actionLabel: "Preset",
        description: `Resets the deck to a known set of cards (${PRESET_DECK_CHARACTERS.join(", ")}) for answer-mode testing. Wipes existing review state first.`,
        disabled: !canRunDataControls || presetActorDeckMutation.isPending,
        id: "preset-deck",
        run: () => {
          presetActorDeckMutation.mutate();
        },
        status: presetDeckStatus,
        title: "Preset test deck",
      },
      {
        actionLabel: "Seed large deck",
        description:
          "Seeds 125 curriculum characters (typically a few hundred cards), with a small Due Today group and the bulk due in 3 days. Use it to test long deck accordions and review performance. Wipes existing review state first.",
        disabled: !canRunDataControls || seedAllStudiedActorDeckMutation.isPending,
        id: "seed-all-studied-deck",
        run: () => {
          seedAllStudiedActorDeckMutation.mutate();
        },
        status: seedAllStudiedDeckStatus,
        title: "Seed large scheduled deck",
      },
      {
        actionLabel: "Clear deck",
        description:
          "Removes deck cards and review history from the actor database. Cannot be undone.",
        disabled: !canRunDataControls || clearActorDeckMutation.isPending,
        id: "clear-deck",
        run: () => {
          clearActorDeckMutation.mutate();
        },
        status: clearDeckStatus,
        title: "Clear deck",
      },
      {
        actionLabel: "Reset data",
        description:
          "Permanently resets device settings and the app and actor databases, then reloads. Cannot be undone.",
        disabled: !canRunDataControls || resetAllDatabasesMutation.isPending,
        id: "reset-data",
        run: () => {
          resetAllDatabasesMutation.mutate();
        },
        status: resetDataStatus,
        title: "Reset data",
      },
    ],
    [
      canRunDataControls,
      clearActorDeckMutation,
      clearDeckStatus,
      isProfiling,
      lastProfilePath,
      openProfileMutation,
      openProfileStatus,
      presetActorDeckMutation,
      presetDeckStatus,
      profilingMutation,
      profilingStatus,
      resetAllDatabasesMutation,
      resetDataStatus,
      seedAllStudiedActorDeckMutation,
      seedAllStudiedDeckStatus,
    ],
  );

  return {
    bundle: __DEV__ ? "development" : "production",
    controls,
  };
}
